import React, { useState, useMemo } from 'react';
import { Plus, Trash2, ChevronRight, Search, X } from 'lucide-react';
import { safeAsync, Badge, emptyMsg, cardWrap, btnPrimary, neuShadowSm, inp } from './crmShared';

const CHANNELS = [
  { id: 'sms', label: 'SMS', color: '#62BB46' },
  { id: 'email', label: 'Email', color: '#2E8B98' }
];

export default function TemplatesTab({ templates, canCreate, loadExtended }) {
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Filter templates based on search query
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const q = searchQuery.toLowerCase();
    return templates.filter(t =>
      t.name.toLowerCase().includes(q) ||
      (t.subject && t.subject.toLowerCase().includes(q)) ||
      (t.body && t.body.toLowerCase().includes(q))
    );
  }, [templates, searchQuery]);

  // Group filtered templates by channel
  const groupedTemplates = useMemo(() => {
    const groups = {};
    CHANNELS.forEach(ch => {
      groups[ch.id] = filteredTemplates.filter(t => t.channel === ch.id);
    });
    return groups;
  }, [filteredTemplates]);

  // When search is active, auto-expand all groups with results
  useMemo(() => {
    if (searchQuery.trim()) {
      const groupsWithResults = new Set();
      CHANNELS.forEach(ch => {
        if (groupedTemplates[ch.id].length > 0) {
          groupsWithResults.add(ch.id);
        }
      });
      setExpandedGroups(groupsWithResults);
    }
  }, [searchQuery, groupedTemplates]);

  const toggleGroup = (channelId) => {
    const newSet = new Set(expandedGroups);
    if (newSet.has(channelId)) {
      newSet.delete(channelId);
    } else {
      newSet.add(channelId);
    }
    setExpandedGroups(newSet);
  };

  const expandAll = () => {
    const allChannelsWithResults = new Set();
    CHANNELS.forEach(ch => {
      if (groupedTemplates[ch.id].length > 0) {
        allChannelsWithResults.add(ch.id);
      }
    });
    setExpandedGroups(allChannelsWithResults);
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const hasAnyResults = Object.values(groupedTemplates).some(arr => arr.length > 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0 }}>Message Templates</h3>
        {canCreate && (
          <button onClick={safeAsync(async () => {
            const name = prompt('Template name:');
            if (!name) return;
            const channel = prompt('Channel (sms or email):', 'sms');
            if (!channel || !['sms','email'].includes(channel)) { alert('Channel must be sms or email'); return; }
            const subject = channel === 'email' ? (prompt('Email subject:') || '') : '';
            const body = prompt('Message body (use {{first_name}}, {{last_name}} for merge fields):');
            if (!body) return;
            await window.zignagenixAPI.createCRMTemplate({ name, channel, subject, body });
            loadExtended();
          })} style={btnPrimary}><Plus size={14} /> Add Template</button>
        )}
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, color: '#6C757D', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Search templates by name, subject, or content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              ...inp,
              paddingLeft: 36,
              paddingRight: searchQuery ? 36 : 12,
              width: '100%'
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: 12,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Clear search"
            >
              <X size={16} color="#6C757D" />
            </button>
          )}
        </div>
      </div>

      {/* Expand/Collapse All Buttons */}
      {hasAnyResults && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button
            onClick={expandAll}
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: '#2E8B98',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline'
            }}
          >
            Expand All
          </button>
          <span style={{ fontSize: 12, color: '#CED4DA' }}>|</span>
          <button
            onClick={collapseAll}
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: '#2E8B98',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline'
            }}
          >
            Collapse All
          </button>
        </div>
      )}

      <div style={cardWrap}>
        {!hasAnyResults ? (
          <div style={emptyMsg}>
            {templates.length === 0
              ? 'No message templates yet. Add templates for SMS and email actions in workflows.'
              : 'No templates match your search.'}
          </div>
        ) : (
          CHANNELS.map(channel => {
            const channelTemplates = groupedTemplates[channel.id];
            const isExpanded = expandedGroups.has(channel.id);

            if (channelTemplates.length === 0) return null;

            return (
              <div key={channel.id}>
                {/* Group Header */}
                <div
                  onClick={() => toggleGroup(channel.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    backgroundColor: '#F8F9FA',
                    cursor: 'pointer',
                    userSelect: 'none',
                    borderBottom: '1px solid #E9ECEF',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#EDEEF1'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F8F9FA'}
                >
                  <ChevronRight
                    size={18}
                    color="#6C757D"
                    style={{
                      transition: 'transform 0.2s ease',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      flexShrink: 0
                    }}
                  />
                  <Badge text={channel.label} color={channel.color} />
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#212529', flex: 1 }}>
                    {channel.label} Templates
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#6C757D',
                      backgroundColor: '#E9ECEF',
                      padding: '2px 8px',
                      borderRadius: 4,
                      flexShrink: 0
                    }}
                  >
                    {channelTemplates.length}
                  </span>
                </div>

                {/* Group Content with Smooth Transition */}
                <div
                  style={{
                    maxHeight: isExpanded ? (channelTemplates.length * 140) : 0,
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease',
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  {channelTemplates.map((t, i) => (
                    <div
                      key={t.id}
                      style={{
                        padding: '12px 16px',
                        borderBottom: i < channelTemplates.length - 1 ? '1px solid #E9ECEF' : 'none',
                        background: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                        transition: 'background-color 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F8F9FA'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FFFFFF'}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, fontSize: 13, color: '#212529' }}>{t.name}</span>
                        </div>
                        {t.subject && <div style={{ fontSize: 12, color: '#6C757D' }}>Subject: {t.subject}</div>}
                        <div style={{ fontSize: 12, color: '#6C757D', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                          {t.body?.substring(0, 200)}{t.body?.length > 200 ? '...' : ''}
                        </div>
                      </div>
                      <button
                        onClick={safeAsync(async () => {
                          if (window.confirm(`Delete template "${t.name}"?`)) {
                            await window.zignagenixAPI.deleteCRMTemplate(t.id);
                            loadExtended();
                          }
                        })}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 5,
                          borderRadius: 6,
                          boxShadow: neuShadowSm,
                          flexShrink: 0,
                          transition: 'all 0.15s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Delete"
                      >
                        <Trash2 size={14} color="#DC3545" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
